/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Sparkles, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Phase 326g — Claude Coach's Tip of the Day card.
 * -------------------------------------------------
 * Renders a personalised 1-2 sentence nudge fetched from
 * /api/student/coachs-tip. Self-hides silently on auth/fetch failure
 * so a broken backend never breaks the student portal layout.
 */
export default function CoachsTipCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async ({ refresh = false } = {}) => {
    if (refresh) setRefreshing(true);
    try {
      const res = await api.get(`/student/coachs-tip${refresh ? '?refresh=true' : ''}`);
      setData(res.data);
    } catch (_) {
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Card
        className="border-amber-200 bg-gradient-to-br from-amber-50 via-yellow-50 to-white overflow-hidden"
        data-testid="coachs-tip-card-loading"
      >
        <CardContent className="p-4 space-y-2">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
        </CardContent>
      </Card>
    );
  }

  if (!data || !data.tip) return null;

  return (
    <Card
      className="border-amber-200 bg-gradient-to-br from-amber-50 via-yellow-50 to-white overflow-hidden relative group"
      data-testid="coachs-tip-card"
    >
      {/* Decorative glow */}
      <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-amber-200/40 blur-2xl pointer-events-none" />
      <CardContent className="p-4 sm:p-5 relative">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center flex-shrink-0 shadow-sm ring-1 ring-amber-300/60">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="text-[10px] sm:text-xs font-bold text-amber-700 uppercase tracking-wider">
                Coach's Tip of the Day
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-amber-700 hover:bg-amber-100 hover:text-amber-900"
                onClick={() => load({ refresh: true })}
                disabled={refreshing}
                title="Refresh tip"
                data-testid="coachs-tip-refresh"
              >
                <RefreshCcw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            <p
              className="text-sm sm:text-[15px] text-slate-800 leading-relaxed"
              data-testid="coachs-tip-body"
            >
              {data.tip}
            </p>
            {data.source === 'fallback' && (
              <div className="text-[10px] text-amber-600/80 mt-1.5">
                Tap refresh once your academy adds attendance, fees or scorecards for a personalised tip.
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
